import { useState } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useModals, useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';
import { useUpdateAccountAddress } from '@/services/db/account/mutations';
import { UpdateAddressModal } from '../components/update-address-modal';
import { addressFromAccount, applyAddressChange, visibleBillingErrors } from '../utils/address-form-helpers';
import { isZipCodeIgnored } from '../utils/address-options';
import { validateBillingAddress } from '../utils/address-validation';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { billingFieldErrors, firstBillingError, type BillingFieldErrors } from '../utils/credit-card-form-validation';
import { useBillingEditGuard } from './use-billing-edit-guard';

/** Bundled so each card form stays under the hook budget. */
export function useCardChargeServices() {
  const { account } = useAuth();
  const { mutateAsync: updateAccountAddress } = useUpdateAccountAddress({ accountId: account.id });
  return {
    account,
    track: useAnalytics().track,
    snackbars: useSnackbars(),
    modals: useModals(),
    stripe: useStripe(),
    elements: useElements(),
    editGuard: useBillingEditGuard(),
    updateAccountAddress,
  };
}

export type CardEntryHandlers = Pick<
  CardEntryFields,
  'onNameInput' | 'onAddressChange' | 'onFieldBlur' | 'setIsCardComplete'
>;

export type CardEntryFields = {
  name: string;
  address: CreditCardFormAddress;
  fieldErrors: BillingFieldErrors;
  visibleErrors: BillingFieldErrors;
  firstError: string | undefined;
  /** A saved card, or new card details that Stripe reports complete. */
  isCardReady: boolean;
  canEditZip: boolean;
  setAddress: (address: CreditCardFormAddress) => void;
  onNameInput: (value: string) => void;
  onAddressChange: (field: keyof CreditCardFormAddress, value: string) => void;
  onFieldBlur: (field: string) => void;
  setIsCardComplete: (complete: boolean) => void;
  onPaymentMethodChange: (useSavedCard: boolean) => void;
};

/** `onInteract` runs on each user change. */
export function useCardEntryFields(useSavedCard: boolean, onInteract: () => void): CardEntryFields {
  const { account } = useAuth();
  const [name, setName] = useState('');
  const [address, setAddress] = useState<CreditCardFormAddress>(() => addressFromAccount(account));
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());

  const fieldErrors = useSavedCard ? {} : billingFieldErrors(name, address);

  return {
    name,
    address,
    fieldErrors,
    visibleErrors: visibleBillingErrors(fieldErrors, touched),
    firstError: firstBillingError(fieldErrors),
    // Stripe can't report completeness in test mode (plain inputs), so treat the card as complete then.
    isCardReady: useSavedCard || IS_STRIPE_TEST_MODE || isCardComplete,
    canEditZip: !isZipCodeIgnored(address.country),
    setAddress,
    onNameInput: (value) => {
      onInteract();
      setName(value);
    },
    onAddressChange: (field, value) => {
      onInteract();
      setAddress((current) => applyAddressChange(current, field, value));
    },
    onFieldBlur: (field) => setTouched((current) => new Set(current).add(field)),
    setIsCardComplete: (complete) => {
      onInteract();
      setIsCardComplete(complete);
    },
    onPaymentMethodChange: (nextUseSavedCard) => {
      if (nextUseSavedCard) {
        setAddress(addressFromAccount(account));
      } else {
        setIsCardComplete(false);
      }
    },
  };
}

export async function chooseBillingAddress(
  accountId: number,
  modals: Pick<ReturnType<typeof useModals>, 'open' | 'waitForClose'>,
  input: CreditCardFormAddress,
): Promise<CreditCardFormAddress | 'aborted'> {
  const validation = await validateBillingAddress(accountId, input);
  if (validation?.correctedAddress == null) return input;

  const modalId = modals.open(UpdateAddressModal, {
    inputAddress: input,
    correctedAddress: validation.correctedAddress,
  });
  const result = await modals.waitForClose(modalId);
  return result.reason === 'confirm' && result.data != null ? result.data : 'aborted';
}

export const CORRECTED_ADDRESS_MESSAGE =
  'We found a possible correction for your address. Select the current address or corrected address.';
